import { useState } from 'react';
import { Input, Table, Tag, Typography } from 'antd';
import { useSizCatalog } from '@/entities/siz';
import { PageSpinner } from '@/shared/ui/PageSpinner';
import type { SizCatalogItem } from '@/entities/siz';
import s from './CatalogTab.module.scss';

const { Text } = Typography;

interface CatalogTabProps {
  focusEmployeeId?: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  clothing: 'Одежда',
  footwear: 'Обувь',
  head_protection: 'Защита головы',
  eye_protection: 'Защита глаз',
  hand_protection: 'Защита рук',
  respiratory_protection: 'Защита дыхания',
  fall_protection: 'Защита от падения',
  hearing_protection: 'Защита слуха',
  other: 'Прочее',
};

const UNIT_LABELS: Record<string, string> = {
  piece: 'шт.',
  pair: 'пар',
  kit: 'компл.',
  set: 'набор',
};

export function CatalogTab(_props: CatalogTabProps) {
  const [search, setSearch] = useState('');
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useSizCatalog({
    q: search || undefined,
  });

  const items = data?.pages.flatMap((page) => page.items) || [];

  const columns = [
    {
      title: 'Код',
      dataIndex: 'code',
      key: 'code',
      width: 120,
    },
    {
      title: 'Наименование',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Категория',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (category: string) => <Tag>{CATEGORY_LABELS[category] || category}</Tag>,
    },
    {
      title: 'Ед. изм.',
      dataIndex: 'unit',
      key: 'unit',
      width: 100,
      render: (unit: string) => UNIT_LABELS[unit] || unit,
    },
    {
      title: 'ГОСТ/ТР ТС',
      dataIndex: 'gostOrTrTs',
      key: 'gostOrTrTs',
      width: 200,
      render: (value?: string) => value || '—',
    },
    {
      title: <span style={{ whiteSpace: 'nowrap' }}>Срок службы&nbsp;(мес.)</span>,
      dataIndex: 'defaultValidityMonths',
      key: 'defaultValidityMonths',
      width: 150,
      render: (value?: number) => value || '—',
    },
  ];

  if (isLoading) return <PageSpinner />;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 12,
        }}
      >
        <Text strong className={s.root__title}>
          Каталог СИЗ
        </Text>
        <Input.Search
          placeholder="Поиск по наименованию..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ width: 300 }}
        />
      </div>
      <Table<SizCatalogItem>
        columns={columns}
        dataSource={items}
        rowKey="id"
        pagination={false}
        size="small"
        scroll={{ x: 'max-content' }}
        onScroll={(e) => {
          const target = e.target as HTMLDivElement;
          if (
            target.scrollHeight - target.scrollTop - target.clientHeight < 100 &&
            hasNextPage &&
            !isFetchingNextPage
          ) {
            fetchNextPage();
          }
        }}
      />
    </div>
  );
}
